import { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { EmailListInput } from './EmailListInput';
import { ShortCodeTag } from './ShortCodeTag';
import { api, ApiError } from '../lib/api';

export interface RenewalNoticeGroup {
  groupId: string;
  company_id: string;
  company_name: string;
  short_code?: string;
  examination_date: string;
  type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
  items: Array<{
    id: string;
    certificate_number: string;
    equipment_name: string;
    internal_id_number: string;
    examination_date: string;
    next_inspection_date: string;
    days_until_due: number;
    status: string;
    type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
    communication_email?: string;
    email_sent_count?: number;
  }>;
  total_count: number;
  overdue_count: number;
  expiring_soon_count: number;
  compliant_count: number;
  earliest_due_date: string;
  communication_email?: string;
  email_sent_count?: number;
}

interface SendRenewalNoticeModalProps {
  open: boolean;
  onClose: () => void;
  group: RenewalNoticeGroup | null;
  onSent?: () => void;
}

export function SendRenewalNoticeModal({
  open,
  onClose,
  group,
  onSent,
}: SendRenewalNoticeModalProps) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [recipient, setRecipient] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [viewMode, setViewMode] = useState<'preview' | 'edit'>('preview');

  useEffect(() => {
    if (!open || !group) return;

    setLoading(true);
    setError('');
    setSuccess('');

    api.renewalEmails
      .previewGroup({
        company_id: group.company_id,
        examination_date: group.examination_date,
        certificate_ids: group.items.map((i) => i.id),
        certificate_type: group.type,
      })
      .then((res: any) => {
        if (res.data) {
          setRecipient(res.data.recipient_email || group.communication_email || '');
          setCcEmails(res.data.cc_emails || '');
          setSubject(res.data.subject || '');
          setBodyText(res.data.body_text || '');
          setBodyHtml(res.data.body_html || '');
        }
      })
      .catch((err: ApiError) => {
        setError(err.message || 'Failed to generate group email preview');
      })
      .finally(() => setLoading(false));
  }, [open, group]);

  const handleSend = async () => {
    if (!group) return;
    if (!recipient || !recipient.includes('@')) {
      setError('Please provide a valid recipient email address.');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const res: any = await api.renewalEmails.sendGroupManual({
        company_id: group.company_id,
        examination_date: group.examination_date,
        certificate_ids: group.items.map((i) => i.id),
        certificate_type: group.type,
        recipient_email: recipient,
        cc_emails: ccEmails,
        subject,
        body_text: bodyText,
        body_html: viewMode === 'edit' ? undefined : bodyHtml,
      });
      setSuccess(
        res.data?.message ||
          res.message ||
          `Group renewal notification (${group.items.length} certificates) successfully sent to ${recipient}!`,
      );

      if (onSent) {
        setTimeout(() => {
          onSent();
        }, 1200);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send group renewal notification');
    } finally {
      setSending(false);
    }
  };

  if (!group) return null;

  const typeLabel =
    group.type === 'FORM_9' ? 'Form 9' : group.type === 'FORM_11' ? 'Form 11' : group.type === 'TRAINING' ? 'Training Cert' : 'Form 10';
  const modalTitle = `Notify Group: ${group.company_name} — ${typeLabel} (${group.examination_date})`;

  return (
    <Modal open={open} onClose={onClose} title={modalTitle} size="xl">
      <div className="flex flex-col max-h-[82vh] space-y-4">
        {/* Header Meta Bar */}
        <div className="card p-3 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-900 text-sm">
                {group.company_name}
              </span>
              {group.short_code && (
                <ShortCodeTag code={group.short_code} />
              )}
              {group.type && (
                <span
                  className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                    group.type === 'FORM_11'
                      ? 'bg-purple-100 text-purple-800 border border-purple-200'
                      : group.type === 'TRAINING'
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      : 'bg-blue-100 text-blue-800 border border-blue-200'
                  }`}
                >
                  {typeLabel}
                </span>
              )}
              <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-slate-100 text-slate-800 border border-slate-200">
                Batch: {group.items.length} Certificate{group.items.length === 1 ? '' : 's'}
              </span>
            </div>

            <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-2">
              <span>📅 Inspected on: <strong>{group.examination_date}</strong></span>
              <span>&bull;</span>
              <span>Earliest Expiry: <strong className="text-red-600">{group.earliest_due_date}</strong></span>
              {group.overdue_count > 0 && (
                <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-red-100 text-red-700">
                  {group.overdue_count} Overdue
                </span>
              )}
              {group.expiring_soon_count > 0 && (
                <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                  {group.expiring_soon_count} Expiring Soon
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-1 bg-gray-200/80 p-0.5 rounded-lg">
            <button
              type="button"
              onClick={() => setViewMode('preview')}
              className={`text-xs px-3 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                viewMode === 'preview' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              👁️ Email Preview
            </button>
            <button
              type="button"
              onClick={() => setViewMode('edit')}
              className={`text-xs px-3 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                viewMode === 'edit' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ✏️ Customize Text
            </button>
          </div>
        </div>


        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-xs text-red-700 flex items-center justify-between">
            <span>⚠️ {error}</span>
            <button type="button" onClick={() => setError('')} className="text-red-500 font-bold ml-2">✕</button>
          </div>
        )}

        {success && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs text-emerald-700 flex items-center justify-between">
            <span>✅ {success}</span>
            <button type="button" onClick={() => setSuccess('')} className="text-emerald-600 font-bold ml-2">✕</button>
          </div>
        )}

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : (
          <div className="space-y-3 flex-1 overflow-y-auto pr-1">
            {/* Recipient & CC Fields */}
            <div className="space-y-3">
              <div>
                <label className="label text-xs">
                  Recipient (Communication Email) <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="e.g. compliance@company.com"
                  className={`input text-xs py-1.5 ${!recipient ? 'border-amber-400 bg-amber-50/40' : ''}`}
                />
                {!recipient && (
                  <span className="text-[11px] text-amber-700 font-medium mt-1 block">
                    ⚠️ No Communication Email configured for this company. Please enter an email address above.
                  </span>
                )}
              </div>

              <div>
                <EmailListInput
                  label="Carbon Copy (CC)"
                  value={ccEmails}
                  onChange={setCcEmails}
                  placeholder="e.g. safety@gngehs.com"
                  helperText="These recipients will receive a copy of this renewal notification."
                />
              </div>
            </div>

            {/* Subject Field */}
            <div>
              <label className="label text-xs">Email Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="input text-xs py-1.5 font-medium"
              />
            </div>

            {/* View Mode: Preview vs Normal Text Editor */}
            {viewMode === 'preview' ? (
              <div>
                <label className="label text-xs">Rendered Email Preview</label>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-slate-50 p-2">
                  <iframe
                    title="Rendered Notification Preview"
                    srcDoc={bodyHtml}
                    className="w-full h-96 bg-white rounded border border-gray-200 shadow-inner"
                  />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label text-xs">Message Text (Plain text &amp; bullet points)</label>
                  <span className="text-[11px] text-gray-500">
                    Paragraphs and bullet points are automatically styled into the outgoing email
                  </span>
                </div>
                <textarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  rows={10}
                  className="input text-xs p-3 leading-relaxed resize-none"
                  placeholder="Type your notification text here..."
                />
              </div>
            )}
          </div>
        )}

        {/* Modal Footer Actions */}
        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary text-xs py-2 px-4"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={sending || loading || !recipient}
            onClick={handleSend}
            className="btn btn-primary text-xs py-2 px-5 font-semibold inline-flex items-center gap-2"
          >
            <span>{sending ? '⏳' : '🚀'}</span>
            <span>{sending ? 'Dispatching Group Email...' : 'Notify Group Now'}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
